import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import size from 'lodash/size';

import { saveToCSV, formatTimestamp } from 'utils/admin-csv';
import {
  AdminSelect,
  AdminTable,
  AdminFormRow,
  AdminInput,
} from 'shared-components';
import {
  getCarts,
  getRefillsCarts,
  getCartEvents,
} from 'services/api/admin-carts';
import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import PageContainer from 'shared-components/PageContainer/PageContainer';
import { Toast, showErrorToast } from 'shared-components/BaseToast/Toast';
import cartsModel from './cartsModel';
import refillsCartsModel from './refillsCartsModel';
import eventsModel from './eventsModel';
import CartsPageModal from './CartsPageModal/CartsPageModal';
import TableButtons from './TableButtons/TableButtons';
import styles from './CartsPage.module.css';

const EMPTY_COPY = {
  noSkusOrAccountCodes: {
    icon: 'fal fa-search',
    title: 'No SKU(s) or Account Code(s) Selected',
    description: 'Select your search criteria to see results.',
  },
  noResults: {
    icon: 'fal fa-ghost dark',
    title: 'No Results Match Your Search',
    description: 'Please clear your search and try again.',
  },
};

const DATA_MODEL = {
  cartsModel,
  refillsCartsModel,
};

const SALE_TYPES = [
  {
    id: 'season',
    name: 'Add Ons',
  },
  {
    id: 'boost',
    name: 'Boost',
  },
  {
    id: 'edit',
    name: 'Edit',
  },
  {
    id: 'refills',
    name: 'reFills',
  },
  {
    id: 'shop',
    name: 'Shop',
  },
];

const getCampaignsByType = (campaigns, type) =>
  campaigns?.filter((campaign) => campaign.type.toLowerCase() === type);

const CartsPage = ({ campaigns }) => {
  const [selectedSaleType, setSelectedSaleType] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [accountCodes, setAccountCodes] = useState('');
  const [skus, setSkus] = useState('');
  const [carts, setCarts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayTable, setDisplayTable] = useState(false);
  const [emptyCopy, setEmptyCopy] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataModel, setSelectedDataModel] = useState(null);

  const isSaleCart = selectedSaleType !== 'refills';
  const campaignsByType = getCampaignsByType(campaigns, selectedSaleType);
  const buttonsDisabled =
    !selectedSaleType ||
    (isSaleCart && !selectedCampaign) ||
    (!accountCodes && !skus);

  useEffect(() => {
    if (!accountCodes && !skus) {
      setEmptyCopy(EMPTY_COPY.noSkusOrAccountCodes);
    }
    if (isSaleCart && !selectedCampaign) {
      setEmptyCopy(EMPTY_COPY.noCampaign);
    }
  }, [selectedCampaign, accountCodes, skus]);

  // reset selectedCampaign when changing campaign type
  useEffect(() => {
    setSelectedCampaign('');
  }, [selectedSaleType]);

  const handleCancel = () => {
    setSelectedSaleType('');
    setSelectedCampaign('');
    setAccountCodes('');
    setSkus('');
    setCarts([]);
    setDisplayTable(false);
  };

  const fetchCarts = async (campaignParam, skusParam, accountCodesParam) => {
    setIsLoading(true);
    try {
      const response = isSaleCart
        ? await getCarts(campaignParam, skusParam, accountCodesParam)
        : await getRefillsCarts(skusParam, accountCodesParam);
      const {
        data: { items },
      } = response;
      if (!size(items)) {
        setEmptyCopy(EMPTY_COPY.noResults);
      }
      const formatted = formatTimestamp(items, 'createdTs');
      setCarts(formatted);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  const fetchCartsCallback = useCallback(() => {
    fetchCarts(selectedCampaign, skus, accountCodes);
  }, [selectedCampaign, skus, accountCodes]);

  const onSubmit = (e) => {
    e.preventDefault();
    setDisplayTable(true);
    setSelectedDataModel(isSaleCart ? 'cartsModel' : 'refillsCartsModel');
    fetchCarts(selectedCampaign, skus, accountCodes);
  };

  const downloadCartsData = () => {
    const filename = isSaleCart
      ? `carts-campaign-${selectedCampaign}`
      : 'refills-carts';
    saveToCSV(DATA_MODEL[selectedDataModel], carts, filename);
  };

  const downloadCartsEvent = async () => {
    try {
      const response = await getCartEvents(
        selectedCampaign,
        skus,
        accountCodes
      );
      const { items: events } = response.data;
      saveToCSV(
        eventsModel,
        events,
        `cart-events-campaign-${selectedCampaign}`
      );
    } catch (error) {
      showErrorToast(
        'CSV Download Unsuccessful',
        'The CSV failed to download. Please try again.'
      );
    }
  };

  const downloadCartsItems = [
    {
      id: 1,
      name: 'Carts Data',
      value: 'carts',
      onClick: downloadCartsData,
    },
    {
      id: 2,
      name: 'Events Data',
      value: 'events',
      onClick: downloadCartsEvent,
    },
  ];

  return (
    <>
      <PageContainer>
        <div className={styles.container}>
          <h2>Carts</h2>
          <form onSubmit={onSubmit}>
            <AdminFormRow>
              <AdminSelect
                name="saleType"
                label="Sale Type"
                list={SALE_TYPES}
                selected={selectedSaleType}
                selectLabel="Select Type"
                handleChange={(e) => setSelectedSaleType(e.target.value)}
                style={{ marginRight: 36 }}
              />
              {selectedSaleType && isSaleCart && (
                <AdminSelect
                  name="campaign"
                  label="Selected Campaign"
                  list={campaignsByType}
                  selected={selectedCampaign}
                  selectLabel="Select a Campaign"
                  handleChange={(e) => setSelectedCampaign(e.target.value)}
                />
              )}
            </AdminFormRow>
            <AdminFormRow>
              <AdminInput
                label="SKU(s)"
                placeholder="Separate multiple skus by commas"
                type="text"
                style={{ width: '486px' }}
                onChange={(e) => setSkus(e.target.value)}
                value={skus}
              />
              <AdminInput
                label="Account Code(s)"
                placeholder="Separate multiple account codes by commas"
                type="text"
                style={{ width: '486px' }}
                onChange={(e) => setAccountCodes(e.target.value)}
                value={accountCodes}
              />
            </AdminFormRow>
            <div>
              <Button
                buttonType={BUTTON_TYPES.PRIMARY}
                style={{ width: '120px' }}
                type="submit"
                disabled={buttonsDisabled}
              >
                Search
              </Button>
              <Button
                style={{ width: '120px' }}
                buttonType={BUTTON_TYPES.CANCEL}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
          {displayTable && (
            <>
              <hr />
              <div>
                <div className={styles.tableHeader}>
                  <h3>Members' Carts</h3>
                  <TableButtons
                    isSaleCart={isSaleCart}
                    disabled={!carts.length}
                    downloadCartsItems={downloadCartsItems}
                    downloadCartsData={downloadCartsData}
                    openModal={() => setModalOpen(true)}
                  />
                </div>
                <AdminTable
                  model={DATA_MODEL[selectedDataModel]}
                  rows={carts}
                  idFieldName="shopUserId"
                  isLoading={isLoading}
                  emptyIconClassName={emptyCopy?.icon}
                  emptyTitle={emptyCopy?.title}
                  emptyDescription={emptyCopy?.description}
                />
              </div>
            </>
          )}
        </div>
      </PageContainer>
      <Toast />
      <CartsPageModal
        open={modalOpen}
        isSaleCart={isSaleCart}
        closeModal={() => setModalOpen(false)}
        fetchCartsCallback={fetchCartsCallback}
      />
    </>
  );
};

CartsPage.propTypes = {
  campaigns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const mapStateToProps = (state) => ({
  campaigns: state.adminState.campaigns,
});

export default connect(mapStateToProps, {})(CartsPage);
